import { useState, useRef, useEffect } from 'react';
import NoneActionCell from '../../../../components/v2/NoneActionCell.jsx';

/**
 * GroupResidentsCell — shows a resident count badge with a popover listing
 * the residents in a group. Follows the same hover/click popover pattern
 * used by DeviceCell.
 *
 * When `onEdit` + `row` are provided, clicking the badge fires `onEdit(row)`
 * (e.g. to open the edit-group drawer) instead of toggling the popover.
 * Hover preview keeps working in either mode.
 *
 * @param {{
 *   residents: Array<{ resident_id, resident_name, area_name, area_type_name }>,
 *   onEdit?: (row) => void,
 *   row?: object,
 * }} props
 */
const GroupResidentsCell = ({ residents, onEdit, row }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

    const computePosition = () => {
        if (popoverRef.current) {
            const rect = popoverRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 200) {
                setPopoverStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, left: `${rect.left}px`, top: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
            } else {
                setPopoverStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`, bottom: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
            }
        }
    };

    const handleOpen = (nextOpen) => {
        if (nextOpen) computePosition();
        setOpen(nextOpen);
    };

  const isClickToEdit = typeof onEdit === 'function';

  // Empty groups keep the muted "None" look but carry the badge's action, so
  // adding the first member does not require another route into the drawer.
  if (!residents || residents.length === 0) {
    return (
      <NoneActionCell
        className="rp-cell-text--muted"
        onClick={isClickToEdit ? () => onEdit(row) : undefined}
        title="Click to edit group"
      />
    );
  }

  const count = residents.length;

  const handleBadgeClick = () => {
    if (isClickToEdit) {
      setOpen(false);
      onEdit(row);
    } else {
      handleOpen(!open);
    }
  };

  return (
    <div className="rp-group-residents-cell" ref={popoverRef}>
      <button
        type="button"
        onClick={handleBadgeClick}
        onMouseEnter={() => handleOpen(true)}
        onMouseLeave={() => handleOpen(false)}
        className="rp-group-residents-badge"
        aria-label={isClickToEdit
          ? `Edit group — ${count} resident${count !== 1 ? 's' : ''}`
          : `${count} resident${count !== 1 ? 's' : ''} in group`}
        title={isClickToEdit ? 'Click to edit group' : undefined}
      >
        <i className="fe fe-users" aria-hidden="true" />
        {count}
      </button>

      {open && (
        <div className="rp-group-residents-popover" style={popoverStyle} role="tooltip">
          <p className="rp-group-residents-popover__title">
            Group Residents
          </p>
          <ul className="rp-group-residents-popover__list">
            {residents.map((r) => (
              <li key={r.resident_id} className="rp-group-residents-popover__item">
                <span className="rp-group-residents-popover__name">{r.resident_name}</span>
                <span className="rp-group-residents-popover__area">
                  {r.area_name}
                  {r.area_type_name && (
                    <span className="rp-area-type-tag rp-area-type-tag--sm">{r.area_type_name}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GroupResidentsCell;
